type TierInput = { quantity?: number | null; exclTax?: number | null } | null | undefined

type PriceInput =
  | {
      exclTax?: number | null
      inclTax?: number | null
      discountExclTax?: number | null
      discountPercentage?: number | null
      referenceExclTax?: number | null
      tiers?: ReadonlyArray<TierInput> | null
    }
  | null
  | undefined

type PendingBestArticleField = {
  bestArticleCalculatedPrice?: number | null
}

export type ProductDisplayPriceInput = {
  price?: PriceInput
  calculatedPrice?: PriceInput
  bestArticlePrice?: number | null
  stock?: {
    packaging?: { quantity?: number | null; incrementQuantity?: number | null } | null
  } | null
}

export type ProductDisplayPriceOptions = {
  withErpPrice?: boolean
}

export type ProductDisplayPrice = {
  price: number
  priceQuantity: number | undefined
  inclTaxPrice: number
  erpPrice: number | undefined
  discountAmount: number
  discountPercentage: number
  referencePrice: number | undefined
  tiersPrices: { quantity: number; exclTax: number }[]
  isCalculatedPrice: boolean
}

function readPendingFields(product: ProductDisplayPriceInput): PendingBestArticleField {
  return product as PendingBestArticleField
}

function toTiersPrices(price: PriceInput): { quantity: number; exclTax: number }[] {
  return (price?.tiers ?? [])
    .filter((tier): tier is NonNullable<TierInput> => !!tier)
    .map((tier) => ({ quantity: tier.quantity ?? 0, exclTax: tier.exclTax ?? 0 }))
}

export function getProductDisplayPrice(
  product: ProductDisplayPriceInput,
  { withErpPrice = false }: ProductDisplayPriceOptions = {},
): ProductDisplayPrice {
  const { price, calculatedPrice } = product
  const { bestArticleCalculatedPrice } = readPendingFields(product)

  const erpExclTax = product.bestArticlePrice || price?.exclTax || 0
  const calculatedExclTax = bestArticleCalculatedPrice || calculatedPrice?.exclTax || 0
  const salesQuantity = product.stock?.packaging?.incrementQuantity ?? 0
  const priceUnitQuantity = product.stock?.packaging?.quantity ?? 1
  const isCalculatedPrice = calculatedExclTax > 0 && salesQuantity > 0
  const hasDistinctPriceUnit = salesQuantity !== priceUnitQuantity

  if (!isCalculatedPrice) {
    return {
      price: erpExclTax,
      priceQuantity: undefined,
      inclTaxPrice: price?.inclTax ?? 0,
      erpPrice: undefined,
      discountAmount: price?.discountExclTax ?? 0,
      discountPercentage: price?.discountPercentage ?? 0,
      referencePrice: price?.referenceExclTax ?? undefined,
      tiersPrices: toTiersPrices(price),
      isCalculatedPrice: false,
    }
  }

  return {
    price: calculatedExclTax,
    priceQuantity: salesQuantity,
    inclTaxPrice: calculatedPrice?.inclTax ?? 0,
    erpPrice: withErpPrice && hasDistinctPriceUnit ? erpExclTax : undefined,
    discountAmount: calculatedPrice?.discountExclTax ?? 0,
    discountPercentage: calculatedPrice?.discountPercentage ?? price?.discountPercentage ?? 0,
    referencePrice: calculatedPrice?.referenceExclTax ?? undefined,
    tiersPrices: toTiersPrices(calculatedPrice),
    isCalculatedPrice: true,
  }
}
